import React, { useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

mapboxgl.accessToken = 'pk.eyJ1Ijoiam9zaGhvb3ZlciIsImEiOiJjbWJmd3Q2bmwyNW5wMmtwdnF0d200enpvIn0.qCUbYphDsIt1fXpNb1ISZA';

const MapboxMap = ({ 
  center = [-98, 39], 
  zoom = 3, 
  style = { width: '100%', height: '100%' }, 
  onParcelDrawn, 
  overlays = [],
  externalParcelCoordinates = null,
  enableTerrain = true,
  terrainExaggeration = 1.2,
  onMapLoad = null,
  onAddressSelected = null  // Callback when user selects an address from search
}) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const geocoderRef = useRef(null);
  const currentParcelCoordsRef = useRef(null); // Track current parcel to prevent unnecessary zooming

  useEffect(() => {
    if (!mapContainer.current) return;
    
    try {
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/outdoors-v11',
        center,
        zoom,
        // Explicitly enable all interactions including trackpad zoom
        scrollZoom: true,
        boxZoom: true,
        dragRotate: true,
        dragPan: true,
        keyboard: true,
        doubleClickZoom: true,
        touchZoomRotate: true
      });
      mapRef.current = map;

      // Add geocoder control with error handling
      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        marker: false,
        placeholder: 'Search for an address or place',
      });
      
      // Add error handling for geocoder
      geocoder.on('error', (e) => {
        console.warn('Geocoder error:', e);
        // Silently handle geocoder errors to prevent unhandled exceptions
      });
      
      // Add result handler to zoom closer to selected address
      geocoder.on('result', (e) => {
        console.log('Geocoder result:', e.result);
        
        // Notify parent of selected address
        if (onAddressSelected && e.result) {
          onAddressSelected({
            fullAddress: e.result.place_name,
            coordinates: e.result.center,
            // Extract address components if available
            address: e.result.address || '',
            text: e.result.text || '',
            context: e.result.context || []
          });
        }
        
        // Zoom to a closer level for parcel-level detail
        map.flyTo({
          center: e.result.center,
          zoom: 18.81, // 5% back out from 19.8 for optimal parcel detail
          speed: 2.4, // Twice as fast as before
          curve: 1,
          easing: (t) => t
        });
      });
      
      geocoderRef.current = geocoder;
      map.addControl(geocoder, 'top-left');

      // Add draw control (polygon only)
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true
        },
        defaultMode: 'draw_polygon'
      });
      map.addControl(draw, 'top-left');
      drawRef.current = draw;

      // Add 3D navigation controls for terrain viewing
      map.addControl(new mapboxgl.NavigationControl({
        visualizePitch: true // Show pitch control for 3D terrain
      }), 'top-right');
      
      // Add fullscreen control
      map.addControl(new mapboxgl.FullscreenControl(), 'top-right');

      // Listen for draw.create event
      map.on('draw.create', (e) => {
        if (onParcelDrawn && e.features && e.features.length > 0) {
          const feature = e.features[0];
          // Track current parcel coordinates to prevent unnecessary zooming on edits
          if (feature.geometry?.coordinates?.[0]) {
            currentParcelCoordsRef.current = JSON.stringify(feature.geometry.coordinates[0]);
          }
          onParcelDrawn(feature);
        }
      });
      
      // Listen for draw.update event (edit/move)
      map.on('draw.update', (e) => {
        if (onParcelDrawn && e.features && e.features.length > 0) {
          const feature = e.features[0];
          // Track current parcel coordinates to prevent unnecessary zooming on edits
          if (feature.geometry?.coordinates?.[0]) {
            currentParcelCoordsRef.current = JSON.stringify(feature.geometry.coordinates[0]);
          }
          onParcelDrawn(feature);
        }
      });
      
      // Listen for draw mode changes to re-enable map interactions
      map.on('draw.modechange', (e) => {
        console.log('Draw mode changed to:', e.mode);
        // Ensure map interactions are enabled
        setTimeout(() => {
          map.dragPan.enable();
          map.scrollZoom.enable();
          map.doubleClickZoom.enable();
        }, 100);
      });

      // Add map error handling
      map.on('error', (e) => {
        console.warn('Map error:', e);
      });

      // Add 3D terrain support when style loads
      map.on('style.load', () => {
        if (!enableTerrain) {
          console.log('🏔️ Terrain disabled by props');
          return;
        }
        
        try {
          console.log('🏔️ Initializing 3D terrain...');
          
          // Add Mapbox terrain DEM source
          map.addSource('mapbox-dem', {
            'type': 'raster-dem',
            'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
            'tileSize': 512,
            'maxzoom': 14
          });
          
          // Enable 3D terrain with configurable exaggeration
          map.setTerrain({ 
            'source': 'mapbox-dem', 
            'exaggeration': terrainExaggeration
          });
          
          // Add atmospheric fog for better 3D effect
          map.setFog({
            'range': [1, 20],
            'color': '#ffffff',
            'horizon-blend': 0.1,
            'high-color': '#87CEEB', // Sky blue
            'space-color': '#000428' // Deep space color
          });
          
          // Add subtle hillshade for terrain depth
          map.addSource('hillshade-source', {
            'type': 'raster-dem',
            'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
            'tileSize': 512
          });
          
          map.addLayer({
            'id': 'hillshade',
            'type': 'hillshade',
            'source': 'hillshade-source',
            'paint': {
              'hillshade-shadow-color': '#473B24',
              'hillshade-highlight-color': '#FBFAF9',
              'hillshade-exaggeration': 0.4, // Subtle hillshading
              'hillshade-accent-color': '#d4b896'
            }
          }, 'waterway-label'); // Add before labels to keep them on top
          
          console.log('✅ 3D terrain initialized successfully');
          
          // Add MapBox built-in 3D buildings layer
          if (map.getLayer('building')) {
            map.removeLayer('building');
          }
          
          // TODO: Replace with Enterprise building data when available
          // For Enterprise: Use enhanced building datasets with better Michigan coverage
          // const enterpriseBuildingSource = 'mapbox://mapbox.enterprise-buildings-v1';
          
          // Add 3D building extrusions for existing buildings
          map.addLayer({
            'id': 'building-3d',
            'source': 'composite',
            'source-layer': 'building',
            'filter': ['==', 'extrude', 'true'],
            'type': 'fill-extrusion',
            'minzoom': 14,
            'paint': {
              'fill-extrusion-color': [
                'case',
                ['has', 'height'], '#aaa', // Buildings with known height
                ['has', 'levels'], '#bbb', // Buildings with level data
                '#ddd' // Buildings without height data (estimated)
              ],
              'fill-extrusion-height': [
                'case',
                ['has', 'height'], ['get', 'height'], // Use actual height if available
                ['has', 'levels'], ['*', ['get', 'levels'], 4], // levels * 4 meters
                [
                  'case',
                  ['>', ['get', 'area'], 1000], 20, // Large buildings: assume 20m (~5 floors)
                  ['>', ['get', 'area'], 500], 12,  // Medium buildings: assume 12m (~3 floors)
                  8 // Small buildings: assume 8m (~2 floors)
                ]
              ],
              'fill-extrusion-base': ['case', ['has', 'min_height'], ['get', 'min_height'], 0],
              'fill-extrusion-opacity': [
                'case',
                ['has', 'height'], 0.7, // More opaque for known heights
                ['has', 'levels'], 0.6, // Medium opacity for level data
                0.4 // More transparent for estimated heights
              ]
            }
          });
          
          console.log('🏢 Added enhanced 3D buildings layer with intelligent height estimation');
          
          // Notify parent component that map is ready with terrain
          if (onMapLoad) {
            onMapLoad(map);
          }
          
        } catch (error) {
          console.warn('⚠️ Could not initialize terrain (likely style incompatibility):', error);
          
          // Still notify parent that map is ready, even without terrain
          if (onMapLoad) {
            onMapLoad(map);
          }
        }
      });

      return () => {
        try {
          // Clean up geocoder event listeners
          if (geocoderRef.current) {
            geocoderRef.current.off('error');
            geocoderRef.current.off('result');
          }
          
          // Remove map
          if (map) {
            map.remove();
          }
        } catch (error) {
          console.warn('Error during map cleanup:', error);
        }
      };
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }, []);

  // Handle external parcel coordinates (for project loading)
  useEffect(() => {
    const map = mapRef.current;
    const draw = drawRef.current;
    
    if (map && draw && externalParcelCoordinates && Array.isArray(externalParcelCoordinates)) {
      const newCoordsString = JSON.stringify(externalParcelCoordinates);
      
      // Check if these coordinates are already displayed (user is editing, not loading new parcel)
      const isAlreadyDisplayed = currentParcelCoordsRef.current === newCoordsString;
      
      if (isAlreadyDisplayed) {
        console.log('🚫 Skipping zoom - parcel already displayed (user editing)');
        return;
      }
      
      console.log('🗺️ Adding external parcel coordinates to map:', externalParcelCoordinates);
      
      try {
        // Clear existing drawings
        draw.deleteAll();
        
        // Add the parcel as a polygon to the draw control
        const parcelFeature = {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [externalParcelCoordinates]
          },
          properties: {}
        };
        
        draw.add(parcelFeature);
        
        // Track the new coordinates
        currentParcelCoordsRef.current = newCoordsString;
        
        // Zoom to the parcel (only for new parcel loads, not edits)
        const bounds = externalParcelCoordinates.reduce((bounds, coord) => {
          return [
            [Math.min(coord[0], bounds[0][0]), Math.min(coord[1], bounds[0][1])], // SW
            [Math.max(coord[0], bounds[1][0]), Math.max(coord[1], bounds[1][1])]  // NE
          ];
        }, [[Infinity, Infinity], [-Infinity, -Infinity]]);
        
        // Add padding to bounds
        const padding = 0.001;
        const paddedBounds = [
          [bounds[0][0] - padding, bounds[0][1] - padding],
          [bounds[1][0] + padding, bounds[1][1] + padding]
        ];
        
        console.log('🎯 Zooming map to parcel bounds:', paddedBounds);
        map.fitBounds(paddedBounds, { 
          padding: 50,
          maxZoom: 18 
        });
        
      } catch (error) {
        console.error('❌ Error adding external parcel to map:', error);
      }
    }
  }, [externalParcelCoordinates]);

  // Handle overlays (custom GeoJSON layers and vector tiles)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    
    try {
      // Add overlays - each in its own try/catch to prevent one failure from blocking others
      overlays.forEach(overlay => {
        try {
          // Remove existing layer/source if present
          if (map.getLayer(overlay.id)) {
            map.removeLayer(overlay.id);
          }
          if (map.getSource(overlay.id)) {
            map.removeSource(overlay.id);
          }
          
          // Handle different overlay types
          if (overlay.type === 'vector' && overlay.source) {
            // Vector tile source (like Regrid)
            map.addSource(overlay.id, overlay.source);
            map.addLayer({
              id: overlay.id,
              type: 'line',
              source: overlay.id,
              'source-layer': overlay['source-layer'],
              minzoom: overlay.minzoom || 0,
              maxzoom: overlay.maxzoom || 24,
              paint: overlay.paint || {},
              layout: overlay.layout || {}
            });
          } else if (overlay.data === null && overlay.type === 'fill-extrusion') {
            // Mapbox built-in layers (like buildings) - uses existing sources
            map.addLayer({
              id: overlay.id,
              type: overlay.type,
              source: overlay.source,
              'source-layer': overlay['source-layer'],
              minzoom: overlay.minzoom || 0,
              maxzoom: overlay.maxzoom || 24,
              filter: overlay.filter || null,
              paint: overlay.paint || {},
              layout: overlay.layout || {}
            });
          } else if (overlay.data) {
            // Standard GeoJSON overlay - validate data first
            const isValidGeoJSON = overlay.data && 
              (overlay.data.type === 'Feature' || overlay.data.type === 'FeatureCollection');
            
            if (!isValidGeoJSON) {
              console.warn(`⚠️ Invalid GeoJSON for overlay ${overlay.id}, skipping`);
              return;
            }
            
            map.addSource(overlay.id, {
              type: 'geojson',
              data: overlay.data
            });
            map.addLayer({
              id: overlay.id,
              type: overlay.type,
              source: overlay.id,
              paint: overlay.paint || {},
              layout: overlay.layout || {}
            });
          }
        } catch (overlayError) {
          console.warn(`⚠️ Error adding overlay ${overlay.id}:`, overlayError.message);
        }
      });
      
      // Cleanup overlays on unmount or overlays change
      return () => {
        try {
          overlays.forEach(overlay => {
            if (map.getLayer(overlay.id)) map.removeLayer(overlay.id);
            if (map.getSource(overlay.id)) map.removeSource(overlay.id);
          });
        } catch (error) {
          console.warn('Error cleaning up overlays:', error);
        }
      };
    } catch (error) {
      console.warn('Error handling overlays:', error);
    }
  }, [overlays]);

  return <div ref={mapContainer} style={style} />;
};

export default MapboxMap;